import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CheckIcon from '@mui/icons-material/Check';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';

import { fromPromotion } from '../../../../store';
import {
  PROMOTION_BANNER_COUNTDOWN_FORMAT_WIDTH,
  PROMOTION_BANNER_SHOW_DESC_WIDTH,
} from '../../../../utils/utils';
import {
  PromoBannerComponentName,
  PromotionCampaignPrefix,
  SingleProductPromotionInfo,
} from '../../../../models';
import bannerAssets from './BannerAssets';
import { PromotionPrice } from './PromotionPrice';

const useStyles = makeStyles((theme => ({
  descriptionContainer: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(4),
    zIndex: 1,
  },
  countdownTextContainer: {
    display: 'flex',
    height: theme.spacing(12),
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: theme.spacing(3),
    lineHeight: theme.spacing(3),
  },
  ellipseBackground: {
    height: theme.spacing(12),
    position: 'absolute',
    borderRadius: theme.spacing(0.75, 0, 0, 0),
    bottom: theme.spacing(0),
    left: theme.spacing(0),
  },
  hourglass: {
    marginRight: theme.spacing(1),
    minWidth: theme.spacing(3.5),
    width: theme.spacing(3.5),
    minHeight: theme.spacing(12),
    height: theme.spacing(12),
    lineHeight: theme.spacing(2),
    transform: 'rotate(20deg)',
  },
  checkIcon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  descriptionText: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 400,
    fontSize: theme.spacing(1.5),
    lineHeight: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
})));

enum CondownFormat {
  LONG = 'long',
  SHORT = 'short',
}

const PromotionCampaignBanner = () => {
  const classes = useStyles();
  const promotionInfo = useSelector(fromPromotion.selectActivePromotion);
  const [showDescription, setShowDescription] = useState(true);
  const [countdownFormat, setCountdownFormat] = useState<string>(CondownFormat.SHORT);

  const intl = useIntl();
  const promotion = promotionInfo as SingleProductPromotionInfo;
  const endDate = promotion.timeWindow?.endDate;
  const { priceConfig, bannerConfig } = promotion;
  const bannerStyle = bannerConfig?.bannerStyle;
  const [countdownText, setCountdownText] = useState('');
  const assets = bannerConfig && bannerAssets[bannerConfig.promotionPrefix]
    ? bannerAssets[bannerConfig.promotionPrefix]
    : bannerAssets[PromotionCampaignPrefix.DEFAULT];

  // @ts-ignore
  const bannerRef = useCallback(node => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver(() => {
        setShowDescription(node.clientWidth > PROMOTION_BANNER_SHOW_DESC_WIDTH);
        setCountdownFormat(
          node.clientWidth > PROMOTION_BANNER_COUNTDOWN_FORMAT_WIDTH
            ? CondownFormat.LONG
            : CondownFormat.SHORT,
        );
      });
      resizeObserver.observe(node);
    }
  }, []);

  dayjs.extend(utc);
  dayjs.extend(timezone);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = dayjs().utc().format();

      const dayDiff = dayjs(endDate).diff(Date.now(), 'd');
      const hourDiff = dayjs(endDate).diff(now, 'h');
      const minDiff = dayjs(endDate).diff(now, 'm');
      const secondDiff = dayjs(endDate).diff(now, 's');

      if (dayDiff <= 0) {
        if (secondDiff <= 0) {
          clearInterval(interval);
        } else {
          setCountdownText(intl.formatMessage(
            { id: `promotion.campaign.countdown.second.${countdownFormat}` },
            {
              h: hourDiff % 60,
              m: minDiff % 60 % 60,
              s: secondDiff % 60,
            },
          ));
        }
      } else {
        setCountdownText(intl.formatMessage(
          { id: `promotion.campaign.countdown.min.${countdownFormat}` },
          {
            d: dayDiff,
            h: hourDiff % 60 % 24,
            m: minDiff % 60,
          },
        ));
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endDate, countdownFormat]);

  const renderLimitedOffer = () => (
    <>
      {assets.get(PromoBannerComponentName.LEFT_BACKGROUND)}
      {!bannerConfig?.hidePromotionPrice
        && (
        <PromotionPrice
          discountPrice={priceConfig?.discountPrice}
          currentPrice={priceConfig?.currentPrice}
          priceUnit={priceConfig?.priceUnit}
        />
        )}
    </>
  );

  const renderCountDownComponent = () => (
    <Typography variant="h4" color="white" className={classes.countdownTextContainer}>
      <Box
        id="promotion-campaign-countdown-text"
        key="promotion-campaign-countdown-text"
        mr={1}
      >
        {countdownText}
      </Box>
      <Box
        id="promotion-campaign-countdown-hourglass"
        key="promotion-campaign-countdown-hourglass"
        width="min(100%, 24px)"
      >
        {assets.get(PromoBannerComponentName.HOUR_GLASS)}
      </Box>
    </Typography>
  );

  const renderDescriptionComponent = () => (
    <>
      <Typography variant="body1" color="white" className={classes.descriptionText}>
        <CheckIcon className={classes.checkIcon} />
        <FormattedMessage id="promotion.campaign.banner.first.description" />
      </Typography>
      <Typography variant="body1" color="white" className={classes.descriptionText}>
        <CheckIcon className={classes.checkIcon} />
        <FormattedMessage id="promotion.campaign.banner.second.description" />
      </Typography>
      <Typography variant="body1" color="white" className={classes.descriptionText}>
        <CheckIcon className={classes.checkIcon} />
        <FormattedMessage id="promotion.campaign.banner.third.description" />
      </Typography>
    </>
  );

  return (
    <Box
      ref={bannerRef}
      id="promotion-campaign-banner"
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      position="relative"
      overflow="hidden"
      width="100%"
      height={theme => bannerStyle?.height ?? theme.spacing(12)}
      borderRadius={theme => theme.spacing(1, 0.75, 0, 0)}
      sx={{
        backgroundColor: bannerStyle?.backgroundColor ?? '#5959EB',
      }}
    >
      <Box
        id="left-background"
        key="left-background"
        width={bannerStyle?.width ?? '100%'}
        alignContent="center"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        {renderLimitedOffer()}
      </Box>
      {assets.get(PromoBannerComponentName.MID_BACKGROUND)
        ? (
          <Box
            id="promotion-campaign-countdown"
            key="promotion-campaign-countdown"
            display="flex"
            justifyContent="center"
            alignItems="center"
            zIndex={1000}
            width="100%"
          >
            {assets.get(PromoBannerComponentName.MID_BACKGROUND)}
          </Box>
        )
        : (
          // show the countdown in the middle if no middle background asset
          <Box
            id="promotion-campaign-countdown"
            key="promotion-campaign-countdown"
            mr={theme => theme.spacing(showDescription ? 35 : 4)}
            display="flex"
            justifyContent="center"
            alignItems="center"
            zIndex={1000}
          >
            {renderCountDownComponent()}
          </Box>
        )}

      {showDescription && (assets.get(PromoBannerComponentName.RIGHT_BACKGROUND)
        ? (
          <Box
            id="promotion-campaign-right-background"
            key="promotion-campaign-right-background"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
          >
            {assets.get(PromoBannerComponentName.RIGHT_BACKGROUND)}
          </Box>
        )
        : (
          // render description texts if no right background asset
          <div className={classes.descriptionContainer}>
            {renderDescriptionComponent()}
          </div>
        )
      )}
    </Box>
  );
};

export default PromotionCampaignBanner;
